import type { TreeItem } from "../types";

const TREE_LOCATION_QUERY_DELIMITER = "::q=";

export interface TreeLocationSearchParams {
  q?: string;
}

export interface TreeIndex {
  rootIds: string[];
  parentById: Map<string, string | null>;
  childrenById: Map<string, string[]>;
  lineageById: Map<string, string[]>;
  nodeById: Map<string, TreeItem>;
  nodeByLocationKey: Map<string, string>;
  /**
   * Entity occurrence nodes keyed by the identity their path carries — root folder plus the
   * trailing `<alias>/<slug>` pair — in tree order. An element's tree node lives at its full
   * occurrence path (folder + parent lineage), but plenty of navigation targets the same element
   * by a shorter path that names only where it lives and what it is: the path a create action
   * returns, `resolveDefaultEntityPath`, a mention, a bookmarked link. Those paths route to the
   * right page, so the sidebar has to reach the same node from them.
   */
  entityNodeIdsByPathIdentity: Map<string, string[]>;
  topLevelRootIdById: Map<string, string>;
  nodeIdsByTopLevelRoot: Map<string, Set<string>>;
}

export interface TreeLocationMatch {
  nodeId: string;
  node: TreeItem;
  lineage: string[];
}

export function getTreeLocationSearchParams(
  searchParams?: Record<string, unknown>
): TreeLocationSearchParams | undefined {
  const q = searchParams?.q;
  if (typeof q !== "string" || q.length === 0) {
    return undefined;
  }

  return { q };
}

export function getTreeLocationKey(
  path: string,
  searchParams?: TreeLocationSearchParams | Record<string, unknown>
): string {
  const q =
    typeof searchParams?.q === "string" && searchParams.q.length > 0 ? searchParams.q : null;
  return q ? `${path}${TREE_LOCATION_QUERY_DELIMITER}${q}` : path;
}

export function stripTreeLocationContext(locationKey: string): string {
  const delimiterIndex = locationKey.indexOf(TREE_LOCATION_QUERY_DELIMITER);
  if (delimiterIndex < 0) {
    return locationKey;
  }

  return locationKey.slice(0, delimiterIndex);
}

function splitLocationPathSegments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

/**
 * The identity an entity path carries regardless of how much of the element's placement it
 * spells out: the root folder it is navigated in, its type alias and its slug. `/solution/field/f`,
 * `/solution/promo/field/f` and `/solution/promo/dataset/d/field/f` all resolve to the same one.
 */
function buildEntityPathIdentity(path: string): string | null {
  const segments = splitLocationPathSegments(path);
  if (segments.length < 3) {
    return null;
  }

  const rootFolderSlug = segments[0];
  const entityAlias = segments[segments.length - 2];
  const entitySlug = segments[segments.length - 1];
  if (!rootFolderSlug || !entityAlias || !entitySlug) {
    return null;
  }

  return `${rootFolderSlug} ${entityAlias} ${entitySlug}`;
}

function countSharedLeadingSegments(left: string[], right: string[]): number {
  const limit = Math.min(left.length, right.length);
  let shared = 0;
  while (shared < limit && left[shared] === right[shared]) {
    shared += 1;
  }
  return shared;
}

/**
 * Resolves an entity path that names a real element but not the occurrence the tree renders it
 * at. When the element occurs more than once (the same KPI under a question and under a
 * dashboard), the occurrence sharing the longest leading path with the requested location wins,
 * then tree order — so a path that already names a folder or a parent stays inside it.
 */
function locateEntityNodeIdByPathIdentity(treeIndex: TreeIndex, path: string): string | undefined {
  const identity = buildEntityPathIdentity(path);
  if (!identity) {
    return undefined;
  }

  const candidateNodeIds = treeIndex.entityNodeIdsByPathIdentity.get(identity);
  if (!candidateNodeIds || candidateNodeIds.length === 0) {
    return undefined;
  }

  if (candidateNodeIds.length === 1) {
    return candidateNodeIds[0];
  }

  const requestedSegments = splitLocationPathSegments(path);
  let bestNodeId: string | undefined;
  let bestSharedSegments = -1;

  for (const candidateNodeId of candidateNodeIds) {
    const candidatePath = treeIndex.nodeById.get(candidateNodeId)?.path;
    if (!candidatePath) {
      continue;
    }

    const sharedSegments = countSharedLeadingSegments(
      requestedSegments,
      splitLocationPathSegments(candidatePath)
    );
    if (sharedSegments > bestSharedSegments) {
      bestNodeId = candidateNodeId;
      bestSharedSegments = sharedSegments;
    }
  }

  return bestNodeId;
}

export function buildTreeIndex(rootFolderTrees: TreeItem[]): TreeIndex {
  const rootIds: string[] = [];
  const parentById = new Map<string, string | null>();
  const childrenById = new Map<string, string[]>();
  const lineageById = new Map<string, string[]>();
  const nodeById = new Map<string, TreeItem>();
  const nodeByLocationKey = new Map<string, string>();
  const entityNodeIdsByPathIdentity = new Map<string, string[]>();
  const topLevelRootIdById = new Map<string, string>();
  const nodeIdsByTopLevelRoot = new Map<string, Set<string>>();

  function visit(
    item: TreeItem,
    parentId: string | null,
    ancestors: string[],
    topLevelRootId: string
  ) {
    const lineage = [...ancestors, item.id];
    const childIds = item.children?.map((child) => child.id) ?? [];

    if (parentId === null) {
      rootIds.push(item.id);
    }

    parentById.set(item.id, parentId);
    childrenById.set(item.id, childIds);
    lineageById.set(item.id, lineage);
    nodeById.set(item.id, item);
    topLevelRootIdById.set(item.id, topLevelRootId);
    const scopeNodeIds = nodeIdsByTopLevelRoot.get(topLevelRootId) ?? new Set<string>();
    scopeNodeIds.add(item.id);
    nodeIdsByTopLevelRoot.set(topLevelRootId, scopeNodeIds);

    if (item.path) {
      const locationKey = getTreeLocationKey(
        item.path,
        item.metadata?.parentQuestionId ? { q: item.metadata.parentQuestionId } : undefined
      );
      nodeByLocationKey.set(locationKey, item.id);

      // Only element occurrences carry an identity; folder and collection paths are matched by
      // their exact location key or by the ancestor walk.
      if (item.metadata?.entitySlug) {
        const identity = buildEntityPathIdentity(item.path);
        if (identity) {
          const identityNodeIds = entityNodeIdsByPathIdentity.get(identity);
          if (identityNodeIds) {
            identityNodeIds.push(item.id);
          } else {
            entityNodeIdsByPathIdentity.set(identity, [item.id]);
          }
        }
      }
    }

    item.children?.forEach((child) => {
      visit(child, item.id, lineage, topLevelRootId);
    });
  }

  rootFolderTrees.forEach((item) => {
    visit(item, null, [], item.id);
  });

  return {
    rootIds,
    parentById,
    childrenById,
    lineageById,
    nodeById,
    nodeByLocationKey,
    entityNodeIdsByPathIdentity,
    topLevelRootIdById,
    nodeIdsByTopLevelRoot,
  };
}

export function locateTreeNodeByLocation(
  treeIndex: TreeIndex,
  locationKey: string
): TreeLocationMatch | null {
  // Exact match (with and without query context)
  let nodeId: string | undefined =
    treeIndex.nodeByLocationKey.get(locationKey) ??
    treeIndex.nodeByLocationKey.get(stripTreeLocationContext(locationKey));

  if (!nodeId) {
    const path = stripTreeLocationContext(locationKey);

    // Identity fallback: the path names an element the tree renders at a longer occurrence path
    // (its parent lineage spelled out). Match the element itself before giving up on it, so the
    // sidebar expands down to the row rather than stopping at the nearest folder.
    nodeId = locateEntityNodeIdByPathIdentity(treeIndex, path);

    // Prefix fallback: walk up the path segments until a registered node is found.
    // This handles assistant child routes, and entity tab/version suffixes, by matching the
    // nearest node — trying both an exact location key and an element identity at each step.
    let slashIdx = path.lastIndexOf("/");
    while (slashIdx > 0 && !nodeId) {
      const prefix = path.slice(0, slashIdx);
      nodeId =
        treeIndex.nodeByLocationKey.get(prefix) ??
        locateEntityNodeIdByPathIdentity(treeIndex, prefix);
      slashIdx = path.lastIndexOf("/", slashIdx - 1);
    }
  }

  if (!nodeId) {
    return null;
  }

  const node = treeIndex.nodeById.get(nodeId);
  const lineage = treeIndex.lineageById.get(nodeId);
  if (!node || !lineage) {
    return null;
  }

  return {
    nodeId,
    node,
    lineage,
  };
}

export function deriveFocusedSiblingByParent(
  treeIndex: TreeIndex,
  lineage: string[]
): Map<string | null, string> {
  const focusedSiblingByParent = new Map<string | null, string>();

  lineage.forEach((nodeId) => {
    const parentId = treeIndex.parentById.get(nodeId) ?? null;
    if (parentId === null) {
      return;
    }

    focusedSiblingByParent.set(parentId, nodeId);
  });

  return focusedSiblingByParent;
}

export function collectDescendantNodeIds(treeIndex: TreeIndex, nodeId: string): Set<string> {
  const descendantNodeIds = new Set<string>();
  const stack = [...(treeIndex.childrenById.get(nodeId) ?? [])];

  while (stack.length > 0) {
    const currentNodeId = stack.pop();
    if (!currentNodeId || descendantNodeIds.has(currentNodeId)) {
      continue;
    }

    descendantNodeIds.add(currentNodeId);
    stack.push(...(treeIndex.childrenById.get(currentNodeId) ?? []));
  }

  return descendantNodeIds;
}

export function deriveAutoCollapsedNodes(
  treeIndex: TreeIndex,
  activeNodeId: string | null,
  manualExpandedNodes: Set<string>
): Set<string> {
  if (!activeNodeId) {
    return new Set();
  }

  const lineage = treeIndex.lineageById.get(activeNodeId);
  if (!lineage || lineage.length === 0) {
    return new Set();
  }

  const focusedSiblingByParent = deriveFocusedSiblingByParent(treeIndex, lineage);
  const autoCollapsedNodes = new Set<string>();

  focusedSiblingByParent.forEach((focusedChildId, parentId) => {
    if (parentId === null) {
      return;
    }

    const siblingIds = treeIndex.childrenById.get(parentId) ?? [];

    siblingIds.forEach((siblingId) => {
      if (siblingId !== focusedChildId && !manualExpandedNodes.has(siblingId)) {
        autoCollapsedNodes.add(siblingId);
      }
    });
  });

  return autoCollapsedNodes;
}

export function normalizeLocationKeyForNode(
  treeIndex: TreeIndex,
  locationKey: string,
  nodeId: string | null
): string {
  if (!nodeId) {
    return stripTreeLocationContext(locationKey);
  }

  const node = treeIndex.nodeById.get(nodeId);
  if (!node?.metadata?.parentQuestionId) {
    return stripTreeLocationContext(locationKey);
  }

  return locationKey;
}
